
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Filter, Check, X, Clock } from 'lucide-react';
import { Card, CardHeader, CardContent, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { UserRole, SubscriptionTier } from '@/types';
import { useToast } from '@/hooks/use-toast';

interface AdminUser {
  id: string;
  email: string;
  displayName: string;
  role: UserRole;
  subscriptionTier: SubscriptionTier;
  isVerified: boolean;
  createdAt: Date;
  lastLoginAt?: Date;
  status: 'active' | 'suspended' | 'pending';
}

interface UserManagementProps {
  users: AdminUser[];
  onUpdateUserStatus: (userId: string, status: 'active' | 'suspended' | 'pending') => void;
}

const UserManagement: React.FC<UserManagementProps> = ({ users, onUpdateUserStatus }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();
  const { toast } = useToast();

  const filteredUsers = users.filter(user => {
    if (!searchQuery) return true;
    
    const query = searchQuery.toLowerCase();
    return (
      user.displayName.toLowerCase().includes(query) ||
      user.email.toLowerCase().includes(query) ||
      user.id.includes(query) ||
      user.role.toLowerCase().includes(query)
    );
  });

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col md:flex-row justify-between md:items-center gap-4">
          <div>
            <CardTitle>User Management</CardTitle>
            <CardDescription>
              Manage all users, including clients and escorts.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={16} />
              <Input 
                className="pl-9 w-[250px]"
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <Button variant="outline" size="icon">
              <Filter size={16} />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>User</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Subscription</TableHead>
              <TableHead>Verified</TableHead>
              <TableHead>Joined</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                  No users found matching your search
                </TableCell>
              </TableRow>
            ) : (
              filteredUsers.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={`https://via.placeholder.com/32?text=${user.displayName.charAt(0)}`} />
                        <AvatarFallback>{user.displayName.charAt(0)}</AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="font-medium">{user.displayName}</div>
                        <div className="text-xs text-muted-foreground">{user.email}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={
                      user.role === 'admin' ? 'border-purple-DEFAULT text-purple-DEFAULT' :
                      user.role === 'escort' ? 'border-pink-DEFAULT text-pink-DEFAULT' :
                      'border-blue-500 text-blue-500'
                    }>
                      {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge className={
                      user.status === 'active' ? 'bg-green-500' :
                      user.status === 'suspended' ? 'bg-red-500' :
                      'bg-amber-500'
                    }>
                      {user.status.charAt(0).toUpperCase() + user.status.slice(1)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={
                      user.subscriptionTier === 'vip' ? 'border-gold-DEFAULT text-gold-DEFAULT' :
                      user.subscriptionTier === 'regular' ? 'border-blue-500 text-blue-500' :
                      user.subscriptionTier === 'teaser' ? 'border-pink-DEFAULT text-pink-DEFAULT' :
                      'border-muted-foreground text-muted-foreground'
                    }>
                      {user.subscriptionTier.charAt(0).toUpperCase() + user.subscriptionTier.slice(1)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {user.isVerified ? (
                      <Check className="h-5 w-5 text-green-500" />
                    ) : (
                      <X className="h-5 w-5 text-muted-foreground" />
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      {new Date(user.createdAt).toLocaleDateString()}
                    </div>
                    {user.lastLoginAt && (
                      <div className="text-xs text-muted-foreground flex items-center">
                        <Clock className="h-3 w-3 mr-1" />
                        Last seen: {new Date(user.lastLoginAt).toLocaleString()}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => navigate(`/profile/${user.id}`)}
                      >
                        View
                      </Button>
                      <Button 
                        variant={user.status === 'suspended' ? 'outline' : 'destructive'} 
                        size="sm"
                        onClick={() => onUpdateUserStatus(
                          user.id, 
                          user.status === 'suspended' ? 'active' : 'suspended'
                        )}
                      >
                        {user.status === 'suspended' ? 'Activate' : 'Suspend'}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

export default UserManagement;
